import React, {useState, useEffect, useRef, useCallback} from 'react';
import useCollabStatus from '../../../hooks/useCollabStatus';
import {useDispatch, useSelector} from 'react-redux';
import {useAuthInfo} from '@propelauth/react';
import {useFlags} from 'launchdarkly-react-client-sdk';
import PropTypes from 'prop-types';
import {
  selectEffectiveOrg,
  selectImagePrerequisitesReady,
  selectStyleGuide,
} from '../../../data/session';

// Context
import {useDrawer} from '../../../context/DrawerContext';
import {useDocumentStyle} from '../../../context/DocumentStyleProvider';

// Custom Components
import {EmptyDocument} from '../../../components/report-workspace/EmptyDocumentLoading';
import AIAssistantFloatingButton from '../../../components/report-workspace/AIAssistantFloatingButton';
import YjsErrorBoundary from '../../../components/YjsErrorBoundary';

// Collaboration
import {TiptapCollabProvider} from '@tiptap-pro/provider';
import {getCollabSocket} from '../../../util/collabSocketManager';
import {useSelf} from '@liveblocks/react/suspense';
import * as Y from 'yjs';

// PDF / Printing
import usePrintDocument from '../../../hooks/usePrintDocument';
import usePrintDocumentV2 from '../../../hooks/usePrintDocumentV2';
import {transformApiSections} from '../../../hooks/useDocumentSections.utils';

// MUI
import {Box, IconButton, Snackbar, Stack, Typography} from '@mui/material';

// Icons
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

// Stylesheets
import './DocumentStyles.css';
import '@liveblocks/react-ui/styles.css';

// Utils
import {useHighlightEventListener} from '../../../components/report-workspace/comments/comment-utils';
import {COPILOT_DRAWER_WIDTH} from '../../../App';
import {BLACK_100, GREY_70, WHITE_100} from '../../../theme';
import {
  hideActionSuccess,
  hideReviewComplete,
  hideActionError,
  setDocumentId as copilotSetDocumentId,
  reset as copilotReset,
  hideTableFormattingSnackbar,
} from '../../../data/copilot';
import _ from 'lodash';
import {getSharedDocumentData} from '../../../util/sharedDocumentStorage';
import {useLocation, useParams, useNavigate} from 'react-router-dom';
import {
  useGetDocumentQuery,
  useGetDocumentCollabTokenQuery,
  useCreateSupportingDocumentsDownloadUrlMutation,
} from '../../../data/documentWorkspaceApi';
import {
  reset as resetDocumentState,
  setDocument,
  setDocumentAccess,
  setReadOnly,
} from '../../../data/documents';
import {useDebugFlags} from '../../../hooks/useDebugFlags';
import Error from '../../../components/Error';

import Document404 from '../../../components/report-workspace/Document404';
import PrintPreparationModal from '../../../components/report-workspace/DocumentTable/PrintPreparationModal';
import {
  sleep,
  trackEvent,
  triggerDownload,
  hideIntercomIcon,
  showIntercomIcon,
} from '../../../util';
import {X} from 'lucide-react';
import {getPdfPrintVisibility} from '../../../featureFlags/pdfPrintVersion';

const LazyEditor = React.lazy(() => import('./DocumentEditor'));
const LazyHeaderFooterModal = React.lazy(() =>
  import('../../../components/report-workspace/HeaderFooterModal').then(
    (module) => ({default: module.DocumentHeaderFooterModal})
  )
);

const styles = {
  review: {
    container: {
      bgcolor: BLACK_100,
      borderRadius: '0.5rem',
    },
    header: {
      color: BLACK_100,
      fontSize: '0.875rem',
      fontWeight: 700,
      lineHeight: '1.25rem',
    },
    label: {
      color: BLACK_100,
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: '1.25rem',
    },
    boldLabel: {
      color: BLACK_100,
      fontSize: '0.75rem',
      fontWeight: 700,
      lineHeight: '1.25rem',
    },
  },
};

const CollaborativeDocument = (props) => {
  const {documentId} = useParams();
  const {isAuthenticated} = useSelector((state) => state.session);
  const effectiveOrg = useSelector(selectEffectiveOrg);

  const {name} = useSelf((me) => me.info);
  const collabSocket = getCollabSocket();
  // In share link mode, we might not have currentOrg, so use shared document orgId instead
  const orgIdForDocument = effectiveOrg?.orgId;
  const shouldFetchToken = documentId && orgIdForDocument && isAuthenticated;

  const {
    data: {token} = {},
    isFetching,
    isError,
    error,
  } = useGetDocumentCollabTokenQuery(
    {documentId},
    {
      skip: !shouldFetchToken,
      // this is to prevent the token from caching between page changes incase the document is closed
      refetchOnMountOrArgChange: true,
    }
  );

  const [doc, setDoc] = useState(null);
  const [provider, setProvider] = useState(null);

  // Tracks whether isFetching has been true at least once since the last
  // documentId change. This prevents creating a provider with a stale cached
  // token when refetchOnMountOrArgChange returns cached data on first render.
  const hasSeenFetchingRef = useRef(false);

  useEffect(() => {
    hasSeenFetchingRef.current = false;
  }, [documentId]);

  useEffect(() => {
    if (isFetching) {
      hasSeenFetchingRef.current = true;
    }
  }, [isFetching]);

  useEffect(() => {
    if (
      !hasSeenFetchingRef.current ||
      isFetching ||
      !token ||
      !orgIdForDocument
    )
      return;

    const documentName = `${orgIdForDocument}:${documentId}`;
    const yDoc = new Y.Doc();
    const yProvider = new TiptapCollabProvider({
      websocketProvider: collabSocket,
      name: documentName,
      document: yDoc,
      user: name,
      token,
    });

    setDoc(yDoc);
    setProvider(yProvider);

    yProvider.attach();

    return () => {
      yProvider.detach();
      yDoc.destroy();
      setDoc(null);
      setProvider(null);
    };
  }, [documentId, token, orgIdForDocument, isFetching]);

  if (isError) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          height: '100vh',
        }}
      >
        {error?.status === 404 ? (
          <Document404 />
        ) : (
          <Error
            hasError={isError}
            message="There was an error loading your document. Please contact support."
          />
        )}
      </Box>
    );
  }

  if (!doc || !provider) {
    return <EmptyDocument loading={true} />;
  }

  return (
    <YjsErrorBoundary>
      <Document {...props} provider={provider} doc={doc} />
    </YjsErrorBoundary>
  );
};

const Document = ({provider, doc}) => {
  const flags = useFlags();
  const {showV2Pdf} = getPdfPrintVisibility(flags);
  const v2PrintDevPreviewOnly = useDebugFlags('pagination.printDevPreview');
  const {accessHelper} = useAuthInfo();
  const {documentId} = useParams();
  const navigate = useNavigate();
  const {isExternalUser, isAuthenticated} = useSelector(
    (state) => state.session
  );
  const {documentId: copilotDocumentId} = useSelector((state) => state.copilot);
  const {document: documentStorage} = useSelector((state) => state.documents);
  const {
    copilotActionSuccess,
    copilotReviewComplete,
    copilotActionMessage,
    copilotActionError,
    copilotErrorMessage,
  } = useSelector((state) => state.copilot);
  const dispatch = useDispatch();
  const isFinal = useSelector((state) => state.documents.document.isFinal);
  const styleGuide = useSelector(selectStyleGuide);

  const effectiveOrg = useSelector(selectEffectiveOrg);
  const imagePrerequisitesReady = useSelector(selectImagePrerequisitesReady);
  const {
    grantedViaParentOrg: accessGrantedViaParentOrg,
    permission = 'viewer',
  } = getSharedDocumentData() ?? {};

  // Document style guide fonts
  const {fontsReady} = useDocumentStyle();

  const canEdit =
    // Document must not be finalized/locked
    !isFinal &&
    // User must have edit access through one of these paths:
    // 1. External user or parent org user with explicit editor permission on this document
    (((isExternalUser || accessGrantedViaParentOrg) &&
      permission === 'editor') ||
      // 2. Direct org member with can_edit_reports permission (driven by propel auth permissions)
      accessHelper.hasPermission(effectiveOrg.orgId, 'can_edit_reports'));

  // Update readOnly state when canEdit changes
  useEffect(() => {
    dispatch(setReadOnly({readOnly: !canEdit}));
  }, [dispatch, canEdit]);

  const {
    data: documentData,
    isFetching: documentIsFetching,
    isError: documentIsError,
    error: documentError,
    isSuccess: documentIsSuccess,
    refetch: documentRefetch,
  } = useGetDocumentQuery(
    {documentId},
    {
      skip: _.isNil(documentId) || _.isNil(effectiveOrg) || !isAuthenticated,
      refetchOnMountOrArgChange: true,
    }
  );

  // Drawers
  const {drawerStates, openDrawer} = useDrawer();
  const copilot = useSelector((state) => state.copilot);

  // Table formatting snackbar
  const {
    showSnackbar: showTableFormattingSnackbar,
    snackbarMessage: tableFormattingSnackbarMessage,
    snackbarType: tableFormattingSnackbarType,
  } = useSelector((state) => state.copilot.tableFormatting);

  const handleCloseTableFormattingSnackbar = useCallback(() => {
    dispatch(hideTableFormattingSnackbar());
  }, [dispatch]);

  const {isConnected, isSaving, isLoadingDocument} = useCollabStatus(
    provider,
    documentId
  );

  // When switching documents, reset stale Redux document state before the
  // editor has a chance to mount. We also re-apply readOnly immediately after
  // the reset so it isn't briefly wrong for viewers: resetDocumentState sets
  // readOnly:false, but the effect that syncs readOnly from canEdit only fires
  // when canEdit changes — if the user's permissions haven't changed, it won't
  // re-run and readOnly stays false.
  useEffect(() => {
    dispatch(resetDocumentState());
    dispatch(setReadOnly({readOnly: !canEdit}));
  }, [documentId, dispatch]); // intentionally omits canEdit — see comment above

  // Check if we have all required data to render the editor (including image CDN
  // cookie setup on *.inscopehq.com — see `selectImagePrerequisitesReady`)
  const isEditorReady =
    !isLoadingDocument &&
    documentIsSuccess &&
    documentData?.sections &&
    fontsReady &&
    imagePrerequisitesReady;

  // Refs
  const contentToPrintRef = useRef(null);

  // Printing hook - handles all print-related state and logic
  // Print error state
  const [printError, setPrintError] = useState(null);

  const handlePrintError = useCallback((error) => {
    console.error('Print failed:', error);
    setPrintError('Unable to prepare document for printing. Please try again.');
  }, []);

  // Refs populated by DocumentEditor once usePagination is ready.
  // Using refs avoids adding these functions as hook dependencies (no re-renders).
  const preparePrintRef = useRef(null);
  const preparePrintV2Ref = useRef(null);
  const restoreFromPrintRef = useRef(null);
  const paginationSnapshotRef = useRef(null);

  const {
    showPrintPreparation,
    beginPrint,
    endPrint,
    cancelPrintPreparation: cancelPrintPreparationBase,
  } = usePrintDocument({
    watermarkText: styleGuide?.watermarkCopy,
    styleGuide,
    documentData,
    contentToPrintRef,
    onPrepareContent: () => preparePrintRef.current?.(),
    onAfterPrint: () => restoreFromPrintRef.current?.(),
    onPrintError: handlePrintError,
  });

  const {
    showPrintPreparation: showPrintPreparationV2,
    beginPrint: beginPrintV2,
    cancelPrintPreparation: cancelPrintPreparationV2Base,
  } = usePrintDocumentV2({
    watermarkText: styleGuide?.watermarkCopy,
    documentData,
    contentToPrintRef,
    onPrepareContent: () => preparePrintV2Ref.current?.(),
    onAfterPrint: () => restoreFromPrintRef.current?.(),
    getPaginationSnapshot: () => paginationSnapshotRef.current?.(),
    onPrintError: handlePrintError,
    devPreviewOnly: v2PrintDevPreviewOnly,
  });

  // Downloading to Word state
  const [isDownloadingToWord, setIsDownloadingToWord] = useState(false);
  const [isDownloadContentReady, setIsDownloadContentReady] = useState(false);
  /** When true, Word export uses version preview editors and skips thread fetch (history drawer). */
  const [isVersionWordExport, setIsVersionWordExport] = useState(false);

  // Wrapper for cancelPrintPreparation that also resets Word download state
  const cancelPrintPreparation = useCallback(() => {
    cancelPrintPreparationBase();
    setIsDownloadingToWord(false);
    setIsDownloadContentReady(false);
    setIsVersionWordExport(false);
  }, [cancelPrintPreparationBase]);
  // Word export table progress
  const [wordExportProgress, setWordExportProgress] = useState({
    progress: 0,
    processedTables: 0,
    totalTables: 0,
  });

  const [hasLoadedDocument, setHasLoadingDocument] = useState(false);
  const _location = useLocation();
  const _navigate = useNavigate();
  const _queryParams = new URLSearchParams(_location.search);
  const _loadSupportingDocuments = _queryParams.get('d') === 't';
  const [
    createSupportingDocumentsDownloadUrl,
    {
      data: supportingDocumentsData,
      isSuccess: supportingDocumentsIsSuccess,
      isError: supportingDocumentsIsError,
      error: supportingDocumentsError,
    },
  ] = useCreateSupportingDocumentsDownloadUrlMutation();
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Document Header/Footer Modal state
  const [headerFooterModal, setHeaderFooterModal] = useState({
    open: false,
    type: 'header', // 'header' or 'footer',
    editors: null,
  });

  // Redirect to documents page if documentId is not provided. Should never happen.
  useEffect(() => {
    if (_.isNil(documentId)) {
      navigate('/documents', {replace: true});
    }
  }, [documentId, navigate]);

  useEffect(() => {
    if (
      documentIsSuccess &&
      documentData?.record &&
      (documentData.record.documentId !== documentStorage.documentId ||
        documentData.record.isFinal !== documentStorage.isFinal ||
        documentData.record.workflowStatus !== documentStorage.workflowStatus ||
        // Also update if headers/footers are missing from storage but present in API response
        (documentData.record.headers && !documentStorage.headers) ||
        (documentData.record.footers && !documentStorage.footers))
    ) {
      dispatch(
        setDocument({
          document: {
            ...documentData.record,
            autoTransitionScheduledAt:
              documentData.record.autoTransitionScheduledAt || null,
            sections: transformApiSections(documentData.sections || []),
            pageNumberRules: documentData.record.pageNumberRules || [],
            settings: {
              ...documentData.record.settings,
              pagination: {
                ...documentData.record.settings?.pagination,
                pageNumberAlignment:
                  documentData.record.settings?.pagination
                    ?.pageNumberAlignment ?? null,
                widowOrphanControlEnabled:
                  documentData.record.settings?.pagination
                    ?.widowOrphanControlEnabled ?? false,
              },
            },
          },
        })
      );
    }
  }, [documentIsSuccess, documentData, documentStorage, dispatch]);

  // Keep sections in sync whenever they change (for auto TOC)
  useEffect(() => {
    if (documentIsSuccess && documentData?.sections) {
      dispatch(
        setDocument({
          document: {
            sections: transformApiSections(documentData.sections),
            pageNumberRules: documentData.record?.pageNumberRules || [],
            settings: {
              ...documentData.record?.settings,
              pagination: {
                ...documentData.record?.settings?.pagination,
                pageNumberAlignment:
                  documentData.record?.settings?.pagination
                    ?.pageNumberAlignment ?? null,
                widowOrphanControlEnabled:
                  documentData.record?.settings?.pagination
                    ?.widowOrphanControlEnabled ?? false,
              },
            },
          },
        })
      );
    }
  }, [
    documentIsSuccess,
    documentData?.sections,
    documentData?.record?.pageNumberRules,
    documentData?.record?.settings?.pagination?.pageNumberAlignment,
    documentData?.record?.pageNumberAlignment,
    dispatch,
  ]);

  useEffect(() => {
    if (documentIsSuccess) {
      dispatch(setDocumentAccess({access: documentData.access}));
    }
  }, [documentIsSuccess, documentData, dispatch]);

  const handleHeaderFooterSave = useCallback(() => {
    documentRefetch();
  }, [documentRefetch]);

  useEffect(() => {
    if (_loadSupportingDocuments && !hasLoadedDocument) {
      const _startTime = new Date().getTime();
      setHasLoadingDocument(true);
      createSupportingDocumentsDownloadUrl({documentId});
      trackEvent('Supporting Documents Loaded', {
        documentId,
        duration: new Date().getTime() - _startTime,
      });
    }
  }, [documentData, hasLoadedDocument, _loadSupportingDocuments]);

  useEffect(() => {
    if (supportingDocumentsIsSuccess) {
      triggerDownload(
        () => ({
          signedUrl: supportingDocumentsData.supportingDocumentsSignedUrl,
        }),
        `${documentData.record.name} - [Supporting Documents].zip`
      );
    } else if (supportingDocumentsIsError) {
      console.error('Error downloading supporting document', {
        error: supportingDocumentsError,
      });
      setErrorMessage(
        'There was an error downloading the supporting documents. Please try again later.'
      );
      setShowErrorMessage(true);
    }
    _navigate(`/documents/${documentId}`);
  }, [
    supportingDocumentsData,
    supportingDocumentsIsSuccess,
    supportingDocumentsIsError,
    supportingDocumentsError,
  ]);

  useEffect(() => {
    if (copilotActionError) {
      setErrorMessage(copilotErrorMessage);
      setShowErrorMessage(true);
    }
  }, [copilotErrorMessage, copilotActionError]);

  useEffect(() => {
    if (!documentIsFetching) {
      if (documentIsError) {
        if (documentError.status !== 401) {
          trackEvent('Document Metadata Load Failed', {
            documentId,
            status: documentError.status,
            refetch: false,
          });
        } else {
          trackEvent('Document Metadata Load Failed', {
            documentId,
            status: documentError.status,
            refetch: true,
          });
          sleep(500).then(() => {
            documentRefetch();
          });
        }
      }
      if (documentIsSuccess) {
        if (documentId !== copilotDocumentId) {
          dispatch(copilotReset());
          dispatch(copilotSetDocumentId({documentId}));
        }
        trackEvent('Document Metadata Load Completed', {documentId});
      }
    }
  }, [documentIsSuccess, documentIsError, documentIsFetching]);

  useHighlightEventListener((highlightId, isClick) => {
    if (isClick && !isFinal) openDrawer('comments');
  });

  // Hide Intercom widget when document page is mounted
  useEffect(() => {
    try {
      hideIntercomIcon();
    } catch (error) {
      console.error('Error hiding Intercom:', error);
    }

    // Show Intercom icon when component unmounts
    return () => {
      try {
        showIntercomIcon();
      } catch (error) {
        console.error('Error showing Intercom:', error);
      }
    };
  }, []);

  const _getErrorState = () => {
    switch (documentError.status) {
      case 404:
        return <Document404 />;
      default:
        return (
          <Error
            hasError={documentIsError}
            message={
              'There was an error loading your document. Please try again later.'
            }
          />
        );
    }
  };

  const _hideActionError = () => {
    dispatch(hideActionError());
    setShowErrorMessage(false);
    setErrorMessage(null);
  };

  const _hideActionSuccess = () => {
    dispatch(hideActionSuccess());
  };

  const _hideReviewComplete = () => {
    dispatch(hideReviewComplete());
  };

  const beginDownloadDocToWord = useCallback(() => {
    setIsVersionWordExport(false);
    setWordExportProgress({
      progress: 0,
      processedTables: 0,
      totalTables: 0,
    });
    setIsDownloadingToWord(true);
    beginPrint({
      skipPrintDialog: true,
      onReady: () => setIsDownloadContentReady(true),
    });
  }, [beginPrint, setWordExportProgress, setIsDownloadingToWord]);

  /** Word export from a historical version preview (no print preparation; uses preview editors). */
  const beginDownloadVersionToWord = useCallback(() => {
    setWordExportProgress({
      progress: 0,
      processedTables: 0,
      totalTables: 0,
    });
    setIsDownloadingToWord(true);
    setIsVersionWordExport(true);
    setIsDownloadContentReady(true);
  }, [
    setWordExportProgress,
    setIsDownloadingToWord,
    setIsDownloadContentReady,
  ]);

  const afterDownloadDocToWord = useCallback(() => {
    // Reset word export progress
    setWordExportProgress({
      progress: 0,
      processedTables: 0,
      totalTables: 0,
    });
    endPrint();
    setIsDownloadingToWord(false);
    setIsDownloadContentReady(false);
    setIsVersionWordExport(false);
  }, [
    setWordExportProgress,
    setIsDownloadingToWord,
    setIsDownloadContentReady,
  ]);

  const onWordExportProgress = useCallback(
    (progress, processedTables, totalTables) => {
      setWordExportProgress({
        progress,
        processedTables,
        totalTables,
      });
    },
    []
  );

  return (
    <React.Fragment>
      <Stack direction="column" width="100%" height="100vh" bgcolor={GREY_70}>
        {!documentIsError && <EmptyDocument loading={!isEditorReady} />}

        {!isLoadingDocument && documentIsError && (
          <Box display={'flex'} flex={1} height={'100%'} alignItems={'center'}>
            {_getErrorState()}
          </Box>
        )}

        {isEditorReady && !documentIsError && (
          <React.Fragment>
            <React.Suspense fallback={<EmptyDocument />}>
              <LazyEditor
                provider={provider}
                doc={doc}
                documentId={documentId}
                documentName={_.get(documentData, 'record.name')}
                canEdit={canEdit}
                isConnected={isConnected}
                isSaving={isSaving}
                isFinal={isFinal}
                contentToPrintRef={contentToPrintRef}
                isDownloadContentReady={isDownloadContentReady}
                preparePrintRef={preparePrintRef}
                preparePrintV2Ref={preparePrintV2Ref}
                restoreFromPrintRef={restoreFromPrintRef}
                paginationSnapshotRef={paginationSnapshotRef}
                handlePrint={beginPrint}
                handlePrintV2={showV2Pdf ? beginPrintV2 : undefined}
                handleDownload={beginDownloadDocToWord}
                handleDownloadVersionToWord={beginDownloadVersionToWord}
                isVersionWordExport={isVersionWordExport}
                afterDownload={afterDownloadDocToWord}
                onWordExportProgress={onWordExportProgress}
                fiscalYear={documentStorage.fiscalYear}
                flags={flags}
                sections={documentData?.sections}
                documentData={documentData}
                setHeaderFooterModal={setHeaderFooterModal}
              />
            </React.Suspense>
            {flags.copilot && !isFinal && (
              <AIAssistantFloatingButton
                isVisible={
                  !!isEditorReady &&
                  !drawerStates.history &&
                  !drawerStates.comments
                }
                onClick={() => openDrawer('copilot')}
                isOpen={drawerStates.copilot}
              />
            )}
          </React.Fragment>
        )}
      </Stack>

      {/* Document Header/Footer Modal */}
      <React.Suspense fallback={<div>Loading...</div>}>
        <LazyHeaderFooterModal
          open={headerFooterModal.open}
          onClose={() =>
            setHeaderFooterModal({...headerFooterModal, open: false})
          }
          documentId={documentId}
          headerFooterType={headerFooterModal.type}
          onSave={handleHeaderFooterSave}
          editors={headerFooterModal.editors}
          sections={documentData?.sections || []}
        />
      </React.Suspense>

      {!isConnected && (
        <Snackbar
          anchorOrigin={{vertical: 'top', horizontal: 'center'}}
          open={!isConnected}
          message="You've lost connection. Editing is disabled until connection is reestablished."
          sx={{
            '&.MuiSnackbar-root': {
              top: '100px',
            },
            '& .MuiSnackbarContent-root': {
              backgroundColor: '#162523',
              borderRadius: '8px',
            },
          }}
        />
      )}

      <Snackbar
        open={copilotActionSuccess}
        autoHideDuration={5000}
        onClose={_hideActionSuccess}
        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
        sx={{marginRight: drawerStates.copilot ? COPILOT_DRAWER_WIDTH : 0}}
        ClickAwayListenerProps={{onClickAway: () => null}}
      >
        <Stack
          direction={'row'}
          sx={{
            backgroundColor: WHITE_100,
            borderRadius: '0.5rem',
            boxShadow: '0px 1px 5px 0px rgba(11, 14, 12, 0.15);',
          }}
          width={'16.25rem'}
          height={'3rem'}
          paddingX={'1rem'}
          alignItems={'center'}
        >
          <Box
            marginRight={'0.75rem'}
            display={'flex'}
            justifyContent={'center'}
            alignItems={'center'}
            padding={'0.25rem'}
            height={'1.25rem'}
            width={'1.25rem'}
            borderRadius={'50%'}
            sx={{
              background:
                'linear-gradient(0deg, #33B980 0%, #33B980 100%), linear-gradient(180deg, rgba(49, 197, 125, 0.70) 0%, #3B768B 100%), #F1F4F2;',
            }}
          >
            <CheckIcon fontSize={'12px'} sx={{color: WHITE_100}} />
          </Box>
          <Typography variant="body1">
            {!_.isNil(copilotActionMessage)
              ? copilotActionMessage
              : 'Item resolved and closed'}
          </Typography>
        </Stack>
      </Snackbar>
      <Snackbar
        open={showErrorMessage}
        autoHideDuration={5000}
        onClose={_hideActionError}
        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
        sx={{marginRight: drawerStates.copilot ? COPILOT_DRAWER_WIDTH : 0}}
        ClickAwayListenerProps={{onClickAway: () => null}}
      >
        <Stack
          direction={'row'}
          sx={{
            backgroundColor: WHITE_100,
            borderRadius: '0.5rem',
            boxShadow: '0px 1px 5px 0px rgba(11, 14, 12, 0.15);',
          }}
          width={'16.25rem'}
          minHeight={'3rem'}
          paddingX={'1rem'}
          paddingY={'0.5rem'}
          alignItems={'center'}
        >
          <ErrorOutlineIcon
            sx={{color: '#D73E3E', fontSize: '1.25rem', marginRight: '0.75rem'}}
          />
          <Typography variant="body1">
            {!_.isNil(errorMessage)
              ? errorMessage
              : 'There was an error processing your request. Please try again later.'}
          </Typography>
        </Stack>
      </Snackbar>
      <Snackbar
        open={copilotReviewComplete}
        autoHideDuration={5000}
        onClose={_hideReviewComplete}
        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
        sx={{marginRight: drawerStates.copilot ? COPILOT_DRAWER_WIDTH : 0}}
        ClickAwayListenerProps={{onClickAway: () => null}}
      >
        <Stack
          direction={'column'}
          spacing={'0.125rem'}
          sx={{
            flexDirection: 'column',
            backgroundColor: WHITE_100,
            borderRadius: '0.5rem',
            boxShadow: '0px 1px 5px 0px rgba(11, 14, 12, 0.15);',
          }}
          width={'16.25rem'}
          paddingX={'1rem'}
          paddingY={'0.5rem'}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography style={styles.review.header}>
              Review complete!
            </Typography>
            <IconButton onClick={_hideReviewComplete}>
              <X size={16} />
            </IconButton>
          </Stack>
          <Stack direction={'row'}>
            <Typography style={styles.review.label}>
              You have{' '}
              <span style={styles.review.boldLabel}>
                {copilot.numOpenItems} items
              </span>{' '}
              to resolve.
            </Typography>
          </Stack>
        </Stack>
      </Snackbar>

      {/* Table Formatting Snackbar */}
      <Snackbar
        open={showTableFormattingSnackbar}
        autoHideDuration={5000}
        onClose={handleCloseTableFormattingSnackbar}
        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
        sx={{marginRight: drawerStates.copilot ? COPILOT_DRAWER_WIDTH : 0}}
        ClickAwayListenerProps={{onClickAway: () => null}}
      >
        <Stack
          direction="column"
          spacing="0.125rem"
          sx={{
            flexDirection: 'column',
            backgroundColor: WHITE_100,
            borderRadius: '0.5rem',
            boxShadow: '0px 1px 5px 0px rgba(11, 14, 12, 0.15);',
          }}
          width="16.25rem"
          paddingX="1rem"
          paddingY="0.5rem"
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography style={styles.review.header}>
              {tableFormattingSnackbarType === 'fetch'
                ? 'Table formatting ready'
                : tableFormattingSnackbarType === 'accept'
                  ? 'Suggestion accepted'
                  : 'Suggestion rejected'}
            </Typography>
            <IconButton onClick={handleCloseTableFormattingSnackbar}>
              <X size={16} />
            </IconButton>
          </Stack>
          <Stack direction="row">
            <Typography style={styles.review.label}>
              {tableFormattingSnackbarMessage}
            </Typography>
          </Stack>
        </Stack>
      </Snackbar>

      {/* Print Error Snackbar */}
      <Snackbar
        open={!!printError}
        autoHideDuration={5000}
        onClose={() => setPrintError(null)}
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
        message={printError}
        sx={{
          '& .MuiSnackbarContent-root': {
            backgroundColor: '#d32f2f',
            borderRadius: '8px',
          },
        }}
      />

      {/* Print Preparation Modal */}
      <PrintPreparationModal
        open={
          showPrintPreparation ||
          showPrintPreparationV2 ||
          (isDownloadingToWord && isVersionWordExport)
        }
        onCancel={
          showPrintPreparationV2
            ? cancelPrintPreparationV2Base
            : cancelPrintPreparation
        }
        isDownloadingToWord={isDownloadingToWord}
        // Word export specific props (Phase 2)
        isWordExportPhase={isDownloadingToWord && isDownloadContentReady}
        wordExportProgress={wordExportProgress.progress}
      />
    </React.Fragment>
  );
};

Document.propTypes = {
  flags: PropTypes.shape({
    copilot: PropTypes.bool.isRequired,
  }),
  provider: PropTypes.object.isRequired,
  doc: PropTypes.object.isRequired,
};

export default CollaborativeDocument;
